import { zodResolver } from '@hookform/resolvers/zod';
import { type AccountSetupDto, AccountSetupSchema } from '@nestjs-boilerplate/types';
import { CheckCircle2, User } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import logo from '@/assets/OKYC logo.webp';
import { TermsConditionsModal } from '@/components/terms-conditions-modal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { COUNTRY_OPTIONS, TITLE_GROUPS, UK_REGULATED_INDUSTRIES } from '@/constants/industries';
import { useAccountSetup } from '../hooks/use-account-setup';

export function AccountSetupForm() {
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { mutate: submitAccountSetup, isPending } = useAccountSetup();

  const form = useForm<AccountSetupDto>({
    resolver: zodResolver(AccountSetupSchema),
    defaultValues: {
      title: '',
      forename: '',
      middleName: '',
      surname: '',
      jobTitle: '',
      industry: 'SOLICITORS', // Default to solicitors (only enabled option)
      companyName: '',
      companyNumber: '',
      companyWebsite: '',
      personalAddressLine1: '',
      personalAddressLine2: '',
      personalTownCity: '',
      personalCounty: '',
      personalPostcode: '',
      personalCountry: 'GB',
      postcode: '',
      addressLine1: '',
      addressLine2: '',
      townCity: '',
      county: '',
      country: 'GB',
      termsAccepted: false,
    },
  });

  function onSubmit(data: AccountSetupDto) {
    submitAccountSetup(data);
  }

  function handleNextClick() {
    if (!form.getValues('termsAccepted')) {
      // Validate all fields then suppress termsAccepted error - show modal only if the rest is valid
      form
        .trigger()
        .then((isValid) => {
          const hasOnlyTermsError =
            !isValid &&
            Object.keys(form.formState.errors).length === 1 &&
            'termsAccepted' in form.formState.errors;

          if (isValid || hasOnlyTermsError) {
            form.clearErrors('termsAccepted');
            setShowTermsModal(true);
          }
        })
        .catch(() => {
          // Validation threw unexpectedly - surface field errors via react-hook-form
        });
      return;
    }
    form.handleSubmit(onSubmit)();
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed left-0 right-0 top-0 z-10 flex h-16 items-center justify-between border-b border-[#D4DAE0] bg-white px-6">
        <img src={logo} alt="OKYC Logo" className="h-8 w-auto" />
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#131313]">
          <User className="h-5 w-5 text-[#131313]" />
        </div>
      </header>

      {/* Welcome Card - Fixed Position */}
      <div className="fixed left-[30px] top-[94px] w-[330px]">
        <div className="rounded-[5px] bg-[#D4DAE04D] p-6">
          <div className="space-y-4">
            <h2 className="text-[20px] font-semibold leading-[120%] text-[#051923]">
              Welcome to Optimal KYC
            </h2>
            <p className="text-[14px] leading-[22px] text-[#051923]">
              Please complete the following steps to finish setting up your account with us:
            </p>

            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#131313]" />
                <span className="text-[14px] font-medium text-[#131313]">
                  Personal & Company Information
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Form {...form}>
        {/* Main Content - Centered */}
        <main className="mx-auto mt-16 max-w-3xl flex-1 px-8 pb-32 pt-8">
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* User Information Section */}
            <section>
              <h3 className="mb-6 text-[18px] font-semibold text-[#051923]">User Information</h3>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select title" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TITLE_GROUPS.map((group, groupIndex) => (
                            <SelectGroup key={group.category}>
                              {groupIndex > 0 && <div className="my-1 border-t border-gray-200" />}
                              <SelectLabel className="text-xs text-gray-500">
                                {group.category}
                              </SelectLabel>
                              {group.titles.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="forename"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Forename</FormLabel>
                      <FormControl>
                        <Input placeholder="Douglas" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="middleName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Middle Name(s)</FormLabel>
                      <FormControl>
                        <Input placeholder="" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="surname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Surname</FormLabel>
                      <FormControl>
                        <Input placeholder="Kreiger" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="jobTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Compliance Officer" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Personal Address — optional. Captured during account setup and
                  kept on the user record. Separate from the company's
                  registered Companies House address (below). */}
              <div className="mt-8">
                <h4 className="mb-4 text-[16px] font-semibold text-[#051923]">
                  Personal Address <span className="font-normal text-[#051923]/60">(Optional)</span>
                </h4>
                <p className="mb-4 text-[14px] text-[#051923]/70">
                  Your home address — used for compliance records and account verification.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="personalAddressLine1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address Line 1</FormLabel>
                        <FormControl>
                          <Input placeholder="42 Rowan Crescent" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="personalAddressLine2"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address Line 2</FormLabel>
                        <FormControl>
                          <Input placeholder="" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="personalTownCity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Town/City</FormLabel>
                        <FormControl>
                          <Input placeholder="Upper Norleigh" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="personalCounty"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>County</FormLabel>
                        <FormControl>
                          <Input placeholder="Greater London" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="personalPostcode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Postcode</FormLabel>
                        <FormControl>
                          <Input placeholder="BS7 3LP" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="personalCountry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {COUNTRY_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </section>

            <Separator />

            {/* Company Information Section */}
            <section>
              <h3 className="mb-6 text-[18px] font-semibold text-[#051923]">Company Information</h3>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Select Your Industry</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {UK_REGULATED_INDUSTRIES.map((industry) => (
                            <SelectItem
                              key={industry.value}
                              value={industry.value}
                              disabled={!industry.enabled}
                              className={!industry.enabled ? 'text-gray-400' : ''}
                            >
                              {industry.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Goodwin and Sons" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="companyNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Companies House Number</FormLabel>
                      <FormControl>
                        <Input placeholder="12345678" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="companyWebsite"
                  render={({ field }) => (
                    <FormItem className="col-span-2 md:col-span-1">
                      <FormLabel>Company Website</FormLabel>
                      <FormControl>
                        <Input placeholder="https://goodwin-sons.co.uk" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            <Separator />

            {/* Company Registered Address — Companies House registered office.
                Visual layout mirrors the Personal Address subsection above;
                postcode lookup is intentionally omitted (no provider wired
                up yet). */}
            <section>
              <h3 className="mb-6 text-[18px] font-semibold text-[#051923]">
                Company Registered Address
              </h3>
              <p className="mb-4 text-[14px] text-[#051923]/70">
                The official address registered with Companies House. This may differ from your
                personal address above.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="addressLine1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 1</FormLabel>
                      <FormControl>
                        <Input placeholder="1 Companies House Way" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="addressLine2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 2 (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="townCity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Town/City</FormLabel>
                      <FormControl>
                        <Input placeholder="Cardiff" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="county"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>County (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="South Glamorgan" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="postcode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Postcode</FormLabel>
                      <FormControl>
                        <Input placeholder="CF14 3UZ" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COUNTRY_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>
          </form>
        </main>

        {/* Footer */}
        <footer className="fixed bottom-0 left-0 right-0 flex items-center justify-between border-t border-[#D4DAE0] bg-white px-6 py-4">
          <FormField
            control={form.control}
            name="termsAccepted"
            render={({ field }) => (
              <FormItem className="flex items-center space-x-3 space-y-0">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={() => setShowTermsModal(true)} />
                </FormControl>
                <div className="leading-none">
                  <FormLabel className="text-[14px] font-normal text-[#051923]">
                    I have read the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="font-semibold underline"
                    >
                      Terms and Conditions
                    </button>{' '}
                    and I am happy to continue using the Optimal KYC application.
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />

          <Button
            type="button"
            onClick={handleNextClick}
            disabled={isPending}
            className="h-[45px] rounded-[5px] bg-[#131313] px-12 text-[18px] font-medium text-white transition-opacity duration-300 ease-out hover:bg-[#131313]/90 disabled:bg-gray-300 disabled:text-gray-500"
          >
            {isPending ? 'Saving...' : 'Next'}
          </Button>
        </footer>
      </Form>

      <TermsConditionsModal
        open={showTermsModal}
        onOpenChange={setShowTermsModal}
        mode="accept"
        onAccept={() => form.setValue('termsAccepted', true)}
      />
    </div>
  );
}
